<template>
    <div>
        <b-row>
            <b-col cols="12" v-for="doc in mergedDocuments" :key="doc.type">
                <b-alert
                    show
                    :variant="getDocumentVariant(doc.status, doc.type)"
                >
                    <b-row v-if="!editSwitches[doc.type]">
                        <b-col cols="12" md="4">
                            <b-button
                                variant="outline-primary"
                                @click="downloadFile(doc.type)"
                                :disabled="doc.status !== 'uploaded'"
                            >
                                <i
                                    class="ri-download-2-line font-size-16 mr-2"
                                ></i>
                                {{ doc.label }}
                            </b-button>
                        </b-col>
                        <b-col
                            cols="12"
                            md="6"
                            class="d-flex justify-content-center"
                        >
                            <template v-if="doc.status === 'uploaded'">
                                <b-button
                                    v-if="!documentApprovals[doc.type]"
                                    variant="success"
                                    class="mr-2"
                                    @click="doApprove(doc.type, 'approved')"
                                    >{{ $t("approve") }}</b-button
                                >
                                <b-button
                                    v-if="!documentApprovals[doc.type]"
                                    variant="danger"
                                    class="mr-2"
                                    @click="doApprove(doc.type, 'declined')"
                                    >{{ $t("reject") }}</b-button
                                >
                                <b-button
                                    v-if="documentApprovals[doc.type]"
                                    :variant="documentApprovals[doc.type]"
                                    class="mr-2"
                                >
                                    {{
                                        documentApprovals[doc.type] ===
                                        "success"
                                            ? $t("approved")
                                            : $t("rejected")
                                    }}
                                </b-button>

                            </template>
                            <template v-if="checkPermission('bisepapplication_adminaddfile')">
                                <b-button
                                    @click="openFileModal(doc.type)"
                                    variant="light"
                                    class="mr-2"
                                >
                                    {{
                                        $t("upload")
                                    }}
                                </b-button>
                            </template>
                        </b-col>
                    </b-row>
                    <b-row v-else>
                        <b-col cols="8">
                            <ValidationObserver
                                :ref="'formValidate_' + doc.type"
                            >
                                <ValidationProvider
                                    :name="doc.type"
                                    rules="required"
                                    v-slot="{ errors }"
                                >
                                    <b-input-group class="mb-0 mt-1">
                                        <b-form-file
                                            v-model="fileInputs[doc.type]"
                                            :placeholder="doc.label"
                                            :state="errors[0] ? false : null"
                                        />
                                    </b-input-group>
                                    <b-form-invalid-feedback
                                        v-if="errors[0]"
                                        v-html="errors[0]"
                                    />
                                </ValidationProvider>
                            </ValidationObserver>
                        </b-col>
                        <b-col cols="4" class="d-flex justify-content-start">
                            <b-button
                                variant="primary"
                                class="mt-1 mr-2"
                                @click="saveFile(doc.type)"
                                >{{ $t("save") }}</b-button
                            >
                            <b-button
                                variant="secondary"
                                class="mt-1 mr-2"
                                @click="toggleEdit(doc.type)"
                                >{{ $t("cancel") }}</b-button
                            >
                        </b-col>
                    </b-row>
                </b-alert>
            </b-col>
        </b-row>
        <CommonModal ref="adminDocuments" :onHideOnlyX="true" @bHideModalHeaderClose="clear">
            <template v-slot:CommonModalContent>
                <validation-observer ref="fileForm">
                    <ValidationProvider name="file" rules="required" v-slot="{valid, errors}">
                        <b-form-group :label="$t('file')">
                            <div class="d-flex custom-file-upload">
                                <b-form-file
                                    v-model="file"
                                    :state="errors[0] ? false : (valid ? true : null)"
                                    :placeholder="$t('select_file')"
                                    :drop-placeholder="$t('drop_file')"
                                    ref="fileInput"
                                ></b-form-file>
                                <b-button variant="outline-secondary"
                                          @click="$refs.fileInput.$el.childNodes[0].click();">
                                    {{ $t('browse') }}
                                </b-button>
                            </div>
                            <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                        </b-form-group>
                    </ValidationProvider>
                    <div class="col-12 mt-3 d-flex justify-content-center justify-items-center">
                        <ProcessingButton
                            :disabled="fileLoading"
                            :processing="fileLoading"
                            @click="saveAdminDocument"
                            :text="$t('save').toLocaleUpperCase('tr-TR')"
                        />
                    </div>
                </validation-observer>
            </template>
        </CommonModal>
    </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import BisepService from "@/services/BisepService";
import ChangePassword from "@/modules/auth/pages/ChangePassword.vue";
import ProcessingButton from "@/components/elements/ProcessingButton.vue";

export default {
    name: "DocumentsTab",
    components: {
        ProcessingButton,
        ChangePassword,
        ValidationObserver,
        ValidationProvider,
    },
    props: {
        formId: {
            type: Number,
            required: true,
        },
        formData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            type:null,
            file:null,
            editSwitches: {},
            fileInputs: {},
            documentApprovals: {},
            fileLoading:false,
        };
    },

    computed: {
        documentParameters() {
            const param = this.$store.getters.getParameter(
                "bisep_applications.documents"
            );
            return param ? param.items : [];
        },
        mergedDocuments() {
            return this.documentParameters?.map((item) => {
                const type = item.code;
                return {
                    type: type,
                    label: this.getLocaleText(item, "name"),
                    status: this.formData.documents
                        ? this.formData.documents[type]
                        : "not_uploaded",
                };
            });
        },
    },
    async mounted() {
        await this.$store.dispatch("fetchParameter", {
            parameterCode: "bisep_applications.documents",
        });
    },
    methods: {
        clear(){
            this.clearForm()
            this.$refs.adminDocuments.$refs.commonModal.hide()
            this.$refs.fileForm.reset()
        },
        clearForm(){
            this.file=null
            this.type=null
        },
        getDocumentVariant(status, type) {
            if (this.documentApprovals[type])
                return this.documentApprovals[type];
            return status === "uploaded" ? "info" : "secondary";
        },
        toggleEdit(type) {
            this.$set(this.editSwitches, type, !this.editSwitches[type]);
            if (!this.editSwitches[type]) {
                this.$set(this.fileInputs, type, null);
            }
        },
         openFileModal (type){
           this.type=type
           this.$refs.adminDocuments.$refs.commonModal.show()
        },
        async saveAdminDocument (){
            const isValid =await this.$refs.fileForm.validate()
            if (!isValid)return
            try {
                this.fileLoading=true
                const formData =new FormData()
                formData.append('file',this.file)
                formData.append('bisep_application_id',this.formId)
                formData.append('type',this.type)
                formData.append('birthdate',this.formData.birthdate)
                formData.append('father_name',this.formData.father_name)
                formData.append('name',this.formData.name)
                formData.append('national_id',this.formData.national_id)
                formData.append('phone',this.formData.phone)
                formData.append('surname',this.formData.surname)
                const res =await BisepService.uploadAdminDocument(formData)
                this.$toast.success(this.$t('proccess_success'))
                this.$emit('loadData')
                this.clear()

            }catch (err){
                this.showErrors(err)
            }finally {
                this.fileLoading=false
            }
        },
        doApprove(type, approve) {
            const data = {
                bisep_application_id: this.formId,
                type: type,
                status: approve,
            };
            BisepService.approveDocument(data)
                .then((response) => {
                    this.$toast.success(
                        this.$t("api." + response.data.message)
                    );
                    this.$set(
                        this.documentApprovals,
                        type,
                        approve ? "success" : "danger"
                    );
                })
                .catch((e) => {
                    this.showErrors(e);
                });
        },
        downloadFile(type) {
            const data = {
                bisep_application_id: this.formId,
                type: type,
            };
            BisepService.adminDownloadFile(data)
                .then((response) => {
                    this._downloadFile(
                        response,
                        type + "." + response.headers.file_extension
                    );
                })
                .catch((e) => {
                    this.showErrors(e);
                });
        },
        async saveFile(type) {
            this.$toast.info(
                "Save functionality to be implemented if required."
            );
            this.toggleEdit(type);
        },
    },
};
</script>
